<template>
    <div class="rounded-2xl border border-neutral-200/20 bg-neutral-50 p-2 md:p-8">
        <!-- Header -->
        <div class="mb-8 p-4 text-center">
            <h2 class="mb-3 text-2xl font-bold text-neutral-900 md:text-3xl">
                {{ title }}
            </h2>
            <p class="mx-auto max-w-2xl text-neutral-600">
                {{ subtitle }}
            </p>
        </div>

        <!-- Carousel -->
        <div class="relative">
            <div class="flex items-center">
                <!-- Left Arrow -->
                <button
                    v-if="showCarouselControls"
                    ref="leftArrow"
                    class="carousel-arrow left-0 z-10"
                    aria-label="Previous partners"
                    tabindex="0"
                    @click="scrollCarousel(-1)"
                    @keydown.enter="scrollCarousel(-1)"
                    @keydown.space.prevent="scrollCarousel(-1)"
                >
                    <svg
                        class="size-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>

                <!-- Carousel Container -->
                <div class="mx-2 flex-1 overflow-hidden">
                    <div
                        ref="carouselTrack"
                        class="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth p-5"
                        @scroll="updateCarouselControls"
                    >
                        <a
                            v-for="partner in partners"
                            :key="partner.id"
                            :href="partner.primaryLink || '#'"
                            class="partner-card w-64 shrink-0 snap-start no-underline visited:no-underline hover:no-underline md:w-72"
                        >
                            <div class="mb-4 flex h-20 items-center justify-center">
                                <img
                                    :src="partner.logoUrl"
                                    :alt="partner.name"
                                    class="max-h-full max-w-full object-contain"
                                    decoding="async"
                                />
                            </div>

                            <h3 class="mb-3 text-center font-semibold text-neutral-900">
                                {{ partner.name }} <br />
                                <span
                                    :class="`border text-xl fi fi-${partner.countryCode.toLowerCase()}`"
                                ></span>
                            </h3>

                            <div
                                v-if="partner.stats"
                                class="space-y-2 text-sm text-neutral-600"
                            >
                                <div
                                    v-if="partner.stats.year"
                                    class="flex justify-between"
                                >
                                    <span>Year</span>
                                    <span class="font-medium">{{ partner.stats.year }}</span>
                                </div>
                                <div
                                    v-if="partner.stats.ridersCountTotal"
                                    class="flex justify-between"
                                >
                                    <span>Riders</span>
                                    <span class="font-medium">{{
                                        formatNumber(partner.stats.ridersCountTotal)
                                    }}</span>
                                </div>
                                <div
                                    v-if="partner.stats.distanceKmTotal"
                                    class="flex justify-between"
                                >
                                    <span>Route</span>
                                    <span class="font-medium">
                                        <FwbTooltip class="flex justify-between">
                                            <template #trigger>
                                                {{ formatNumber(partner.stats.distanceKmMax) }}
                                                km
                                            </template>
                                            <template #content>
                                                <div>Distance in the longest class</div>
                                            </template>
                                        </FwbTooltip>
                                    </span>
                                </div>
                                <div
                                    v-if="partner.stats.distanceDrivenKmTotal"
                                    class="flex justify-between"
                                >
                                    <span>Distance tracked</span>
                                    <span class="font-medium"
                                        >{{ formatNumber(partner.stats.distanceDrivenKmTotal) }} km</span
                                    >
                                </div>
                            </div>
                        </a>
                    </div>
                </div>

                <!-- Right Arrow -->
                <button
                    v-if="showCarouselControls"
                    ref="rightArrow"
                    class="carousel-arrow right-0 z-10"
                    aria-label="Next partners"
                    tabindex="0"
                    @click="scrollCarousel(1)"
                    @keydown.enter="scrollCarousel(1)"
                    @keydown.space.prevent="scrollCarousel(1)"
                >
                    <svg
                        class="size-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            </div>
            <div class="text-right text-xs">
                *Rotating selection of real public events using RoadbookRally.com
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, nextTick } from "vue";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";

const props = defineProps({
    partners: { type: Array, required: true },
    title: { type: String, default: "Trusted by rally organizers worldwide" },
    subtitle: { type: String, default: "" },
});

// Refs for carousel functionality
const carouselTrack = ref(null);
const leftArrow = ref(null);
const rightArrow = ref(null);
const showCarouselControls = ref(false);

// Carousel functionality
function scrollCarousel(direction) {
    if (!carouselTrack.value) return;

    const scrollAmount = 200;
    carouselTrack.value.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
    });
}

function updateCarouselControls() {
    if (!carouselTrack.value) return;

    const { scrollLeft, scrollWidth, clientWidth } = carouselTrack.value;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

    showCarouselControls.value = canScrollLeft || canScrollRight;
}

// Number formatting utility
function formatNumber(num) {
    return new Intl.NumberFormat().format(num);
}

// Initialize carousel controls
onMounted(() => {
    nextTick(() => {
        updateCarouselControls();
    });
});
</script>

<style scoped>
.partner-card {
    @apply rounded-2xl border border-neutral-200   bg-white/70  backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer;
}

.partner-card:hover {
    @apply transform scale-105;
}

.carousel-arrow {
    @apply rounded-full border border-neutral-200   bg-white/70  backdrop-blur-sm p-2 shadow-sm hover:shadow-md transition-all duration-200 text-neutral-700   hover:text-neutral-900;
}

.carousel-arrow:focus {
    @apply outline-none ring-2 ring-blue-500 ring-offset-2;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
